import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { Add as AddIcon, Warning as WarningIcon, PersonAdd as PersonAddIcon } from "@mui/icons-material";
import { useAuth } from "../../../hooks/useAuth";
import { usePermissions } from "../../../hooks/usePermissions";
import { RoleListMUI } from "../components/RoleListMUI";
import { RoleFormMUI } from "../components/RoleFormMUI";
import { AdminUserListMUI } from "../components/AdminUserListMUI";
import { AdminUserFormMUI } from "../components/AdminUserFormMUI";
import { ToastContainer, toast } from "react-toastify";

interface Role {
  _id?: string;
  name: string;
  displayText: string;
  isActive: boolean;
  permissions?: any[];
  createdAt?: string;
  updatedAt?: string;
}

export const RoleManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [adminRefreshTrigger, setAdminRefreshTrigger] = useState(0);

  // Check permissions for role management page
  const { hasAnyPermission: canAccessRoleManagement } = usePermissions('/role-management');

  if (!canAccessRoleManagement) {
    return (
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        <Paper
          sx={{
            mt: 4,
            p: 3,
            backgroundColor: "error.light",
            color: (theme) => theme.palette.error.contrastText,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
          elevation={3}
        >
          <WarningIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h6" gutterBottom>
              Zugriff verweigert!
            </Typography>
            <Typography variant="body1">
              Sie haben nicht die erforderlichen Berechtigungen, um auf diese Seite
              zuzugreifen. Nur Super-Administratoren können Rollen verwalten.
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  const handleRoleEdit = (role: Role) => {
    setEditingRole(role);
    setShowRoleForm(true);
  };

  const handleRoleFormClose = () => {
    setShowRoleForm(false);
    setEditingRole(null);
  };

  const handleRoleFormSuccess = () => {
    setShowRoleForm(false);
    setEditingRole(null);
    setRefreshTrigger((prev) => prev + 1);
    toast.success("Rolle erfolgreich gespeichert!");
  };

  const handleRoleUpdated = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleAdminFormSuccess = () => {
    setShowAdminForm(false);
    setAdminRefreshTrigger((prev) => prev + 1);
    toast.success("Administrator erfolgreich erstellt!");
  };

  const handleTabChange = (event: any, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1">
          Rollenverwaltung & Administratoren
        </Typography>
        {activeTab === 0 ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowRoleForm(true)}
          >
            Neue Rolle erstellen
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={() => setShowAdminForm(true)}
          >
            Neuen Administrator erstellen
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3 }} elevation={2}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Rollen" />
          <Tab label="Administratoren" />
        </Tabs>

        <Box mt={3}>
          {activeTab === 0 ? (
            <RoleListMUI
              onEditRole={handleRoleEdit}
              onRoleUpdated={handleRoleUpdated}
              refreshTrigger={refreshTrigger}
            />
          ) : (
            <AdminUserListMUI refreshTrigger={adminRefreshTrigger} />
          )}
        </Box>
      </Paper>

      <RoleFormMUI
        role={editingRole}
        open={showRoleForm}
        onClose={handleRoleFormClose}
        onSuccess={handleRoleFormSuccess}
      />

      <AdminUserFormMUI
        open={showAdminForm}
        onClose={() => setShowAdminForm(false)}
        onSuccess={handleAdminFormSuccess}
      />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
      />
    </Container>
  );
};
